/**
 * The in-world clock.
 *
 * Fixed rather than `Date.now()` so `in the last 7 days` selects the same
 * rows today, tomorrow and in a screenshot from last week. The fixture data
 * is generated against this same instant.
 *
 * Lives here rather than beside the fixture adapter because `reportingNow()`
 * below has to name it, and a screen may not import from `adapters/fixture`
 * (#163). Only fixture code and `reportingNow()` read it; in a build with
 * `VITE_API_URL` set the ternary below folds to `Date.now()` and nothing is
 * left referencing it, so it is dropped from the bundle.
 */
export const FIXTURE_NOW = Date.parse("2026-09-09T00:00:00.000Z");

/**
 * What "now" means to a screen — a dashboard tile, a report's day-bucketed
 * chart, a task's overdue date, a CSV's filename.
 *
 * Fixed at the fixture's in-world instant while there is no API, the same way
 * every fixture read already does — every screenshot and every fixture
 * order's "today" stays the same run to run. Once `VITE_API_URL` is set,
 * orders carry real dates spread up to the real instant, so a 30/60-day
 * window has to end there too: bucketing against the fixture clock would
 * silently drop every live order newer than 2026-09-09 (`age < 0` in
 * `aggregateRevenueByDay`), which is a chart that renders and is simply
 * wrong rather than one that visibly fails.
 *
 * `only the caller knows which clock is live` is `aggregateRevenueByDay`'s
 * own doc comment — this is that one decision, made once so every screen
 * agrees on it.
 */
export function reportingNow(): number {
  return import.meta.env.VITE_API_URL ? Date.now() : FIXTURE_NOW;
}
